import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import useLocaleDateFormatter from "../../../hooks/useLocaleDateFormatter";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import { resolveUserAvatarUrl, resolveUserDisplayName, resolveUserUsername } from "../../../utils/userIdentity";
import CommentBubble from "../CommentBubble";
import CommentComposer from "../CommentComposer";
import groupComments from "./groupComments";

import "./index.scss";

const PostComments = ({
    isVisible,
    comments,
    commentsCount,
    isExpanded,
    isLoading,
    canComment,
    commentValue,
    onCommentChange,
    onCommentKeyDown,
    onSubmitComment,
    onStartReply,
    onEditComment,
    onDeleteComment,
    replyTo,
    onCancelReply,
    composerRef,
    onLoadAll,
    onCollapse,
    postAuthorId,
    currentUserId,
    readOnly = false,
    className = "",
}) => {
    const { t } = useTranslation();
    const { formatTime, formatDateTime } = useLocaleDateFormatter();
    const viewer = useSelector((state) => state?.auth?.user || null);
    const [expandedReplies, setExpandedReplies] = useState(() => new Set());

    const handleExpandReplies = useCallback((parentId) => {
        if (!parentId) {
            return;
        }
        setExpandedReplies((prev) => {
            const next = new Set(prev);
            next.add(parentId);
            return next;
        });
    }, []);

    if (!isVisible) {
        return null;
    }

    const hasComments = comments.length > 0;
    const { topLevel, repliesByParent } = groupComments(comments);
    const topLevelIds = new Set(topLevel.map((comment) => comment?.id).filter(Boolean));
    const orphanReplies = Object.entries(repliesByParent)
        .filter(([parentId]) => !topLevelIds.has(parentId))
        .flatMap(([, replies]) => replies);
    const showLoadMore = !readOnly && commentsCount > comments.length && !isExpanded;

    if (!hasComments && !canComment) {
        return null;
    }

    const headerCount = t("home.postComments.count", { count: commentsCount });
    const normalizedPostAuthorId = postAuthorId != null ? String(postAuthorId) : null;

    const viewerId = (() => {
        if (readOnly) {
            return null;
        }
        if (currentUserId != null) {
            return String(currentUserId);
        }
        return viewer?.id != null ? String(viewer.id) : null;
    })();
    const viewerName = resolveUserDisplayName(viewer, "");
    const rawViewerAvatarUrl = resolveUserAvatarUrl(viewer);
    const viewerAvatarUrl = rawViewerAvatarUrl
        ? resolveMediaUrl(rawViewerAvatarUrl)
        : null;

    let secondaryActionLabel = null;
    let secondaryActionHandler = null;
    if (showLoadMore) {
        secondaryActionLabel = isLoading
            ? t("home.postComments.loading")
            : t("home.postComments.viewAll");
        secondaryActionHandler = onLoadAll;
    } else if (isExpanded) {
        secondaryActionLabel = t("home.postComments.collapse");
        secondaryActionHandler = onCollapse;
    } else if (hasComments) {
        secondaryActionLabel = t("home.postComments.sortLabel");
        secondaryActionHandler = null;
    }

    const renderCommentBubble = (comment, { isReply = false } = {}) => {
        const author = comment?.author || {};
        const displayName = resolveUserDisplayName(author, t("home.postCard.comments.anonymousAuthor"));
        const commentAuthorId = author.id
            || author.userId
            || comment?.authorId
            || comment?.userId
            || null;
        const username = resolveUserUsername(author);
        const handle = username ? `@${username}` : "";
        const rawAvatarUrl = resolveUserAvatarUrl(author);
        const avatarUrl = rawAvatarUrl
            ? resolveMediaUrl(rawAvatarUrl)
            : null;
        const createdAtDate = comment?.createdAt
            ? new Date(comment.createdAt)
            : null;
        const validDate = createdAtDate && !Number.isNaN(createdAtDate.getTime())
            ? createdAtDate
            : null;
        const timestamp = validDate ? formatTime(validDate) : "";
        const timestampLabel = validDate ? formatDateTime(validDate) : "";
        const timestampDateTime = validDate ? validDate.toISOString() : "";
        const text = (comment?.content || "").trim();
        const isAuthor = Boolean(
            normalizedPostAuthorId
                && commentAuthorId
                && String(commentAuthorId) === normalizedPostAuthorId
        );
        const canManageComment = Boolean(
            !readOnly
                && viewerId
                && commentAuthorId
                && String(commentAuthorId) === viewerId
                && typeof onEditComment === "function"
                && typeof onDeleteComment === "function"
        );

        return (
            <CommentBubble
                key={comment.id || comment.createdAt}
                commentId={comment.id}
                userId={commentAuthorId ? String(commentAuthorId) : null}
                authorName={displayName}
                authorHandle={handle}
                avatarUrl={avatarUrl}
                avatarAlt={displayName}
                content={text}
                timestamp={timestamp}
                timestampLabel={timestampLabel}
                timestampDateTime={timestampDateTime}
                isAuthor={isAuthor}
                isReply={isReply}
                canManage={canManageComment}
                onReply={readOnly || isReply ? null : () => onStartReply?.(comment)}
                onEdit={(nextContent) => onEditComment?.(comment, nextContent)}
                onDelete={() => onDeleteComment?.(comment)}
                readOnly={readOnly}
            />
        );
    };

    return (
        <section className={classNames("post-comments", className)} aria-label={headerCount}>
            <div className="post-comments__head">
                <span className="post-comments__title">{headerCount}</span>
                {secondaryActionLabel && (
                    secondaryActionHandler ? (
                        <button
                            type="button"
                            className="post-comments__sort"
                            onClick={secondaryActionHandler}
                            disabled={isLoading}
                        >
                            <span>{secondaryActionLabel}</span>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>
                    ) : (
                        <span className="post-comments__sort post-comments__sort--static">
                            <span>{secondaryActionLabel}</span>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </span>
                    )
                )}
            </div>

            {hasComments && (
                <div className="post-comments__list" role="list">
                    {topLevel.map((comment) => {
                        const replies = repliesByParent[comment.id] || [];
                        const isExpanded = expandedReplies.has(comment.id);
                        const hiddenCount = replies.length > 1 && !isExpanded ? replies.length - 1 : 0;
                        const visibleReplies = hiddenCount > 0 ? replies.slice(-1) : replies;

                        return (
                            <React.Fragment key={comment.id || comment.createdAt}>
                                {renderCommentBubble(comment)}
                                {replies.length > 0 && (
                                    <div className="post-comments__replies" role="list">
                                        {hiddenCount > 0 && (
                                            <button
                                                type="button"
                                                className="post-comments__view-more"
                                                onClick={() => handleExpandReplies(comment.id)}
                                            >
                                                <span className="post-comments__view-more-rule" aria-hidden="true" />
                                                <span>
                                                    {t("home.postComments.viewMoreReplies", { count: hiddenCount })}
                                                </span>
                                            </button>
                                        )}
                                        {visibleReplies.map((reply) => renderCommentBubble(reply, { isReply: true }))}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                    {orphanReplies.length > 0 && (
                        <div className="post-comments__replies post-comments__replies--orphan" role="list">
                            {orphanReplies.map((reply) => renderCommentBubble(reply, { isReply: true }))}
                        </div>
                    )}
                </div>
            )}

            {canComment && (
                <CommentComposer
                    ref={composerRef}
                    className="post-comments__composer"
                    viewerId={viewerId}
                    viewerName={viewerName}
                    viewerAvatarUrl={viewerAvatarUrl}
                    value={commentValue}
                    onChange={onCommentChange}
                    onKeyDown={onCommentKeyDown}
                    onSubmit={onSubmitComment}
                    replyTo={replyTo}
                    onCancelReply={onCancelReply}
                />
            )}
        </section>
    );
};

PostComments.propTypes = {
    isVisible: PropTypes.bool,
    comments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        createdAt: PropTypes.string,
        content: PropTypes.string,
        parentId: PropTypes.string,
        author: PropTypes.shape({
            displayName: PropTypes.string,
            username: PropTypes.string,
            profilePicture: PropTypes.string,
            id: PropTypes.string,
            userId: PropTypes.string,
        }),
        authorId: PropTypes.string,
        userId: PropTypes.string,
    })),
    commentsCount: PropTypes.number,
    isExpanded: PropTypes.bool,
    isLoading: PropTypes.bool,
    canComment: PropTypes.bool,
    commentValue: PropTypes.string,
    onCommentChange: PropTypes.func,
    onCommentKeyDown: PropTypes.func,
    onSubmitComment: PropTypes.func,
    onStartReply: PropTypes.func,
    onEditComment: PropTypes.func,
    onDeleteComment: PropTypes.func,
    replyTo: PropTypes.shape({
        commentId: PropTypes.string.isRequired,
        authorName: PropTypes.string.isRequired,
        authorUsername: PropTypes.string.isRequired,
    }),
    onCancelReply: PropTypes.func,
    composerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
    onLoadAll: PropTypes.func,
    onCollapse: PropTypes.func,
    postAuthorId: PropTypes.string,
    currentUserId: PropTypes.string,
    readOnly: PropTypes.bool,
    className: PropTypes.string,
};

PostComments.defaultProps = {
    isVisible: false,
    comments: [],
    commentsCount: 0,
    isExpanded: false,
    isLoading: false,
    canComment: false,
    commentValue: "",
    onCommentChange: () => {},
    onCommentKeyDown: () => {},
    onSubmitComment: () => {},
    onStartReply: null,
    onEditComment: null,
    onDeleteComment: null,
    replyTo: null,
    onCancelReply: () => {},
    composerRef: null,
    onLoadAll: () => {},
    onCollapse: () => {},
    postAuthorId: null,
    currentUserId: null,
    readOnly: false,
    className: "",
};

export default PostComments;
